import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

/**
 * Owns the per-list-screen state the handoff specifies: activeView, filters, query,
 * sort, page, selectedIds, drawerRecord — and drives server-side fetching from it.
 *
 * `fetcher(params)` receives:
 *   { view, page, limit, sort, direction, search, ...filters }
 * and should resolve to `{ items, total }` (a bare array is also accepted, in which
 * case the list is treated as a single unpaginated page).
 */
export default function useListState({
  views = [],
  fetcher,
  countsFetcher,
  initialSort = null,
  initialFilters = {},
  limit = 25,
  searchDebounce = 250,
}) {
  const [activeView, setActiveView] = useState(0);
  const [filters, setFilters] = useState(initialFilters);
  // Per-column filters, keyed by column: { transaction_count: { op: 'gte', value: '5' } }.
  // One filter per column, which is what a single header menu can express.
  const [columnFilters, setColumnFilters] = useState({});
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sort, setSort] = useState(initialSort);
  const [page, setPage] = useState(1);
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [drawerRecord, setDrawerRecord] = useState(null);

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const requestSeq = useRef(0);

  useEffect(() => {
    const id = setTimeout(() => setDebouncedQuery(query), searchDebounce);
    return () => clearTimeout(id);
  }, [query, searchDebounce]);

  // Any change to what is being asked for resets to page 1.
  useEffect(() => {
    setPage(1);
  }, [activeView, debouncedQuery, filters, columnFilters, sort]);

  const params = useMemo(() => {
    const view = views[activeView];
    return {
      view: view?.key ?? view?.label ?? null,
      page,
      limit,
      sort: sort?.column ?? null,
      direction: sort?.direction ?? null,
      search: debouncedQuery || null,
      // Repeated `f=<column>:<op>:<value>` -- the shared list contract's filter form.
      f: Object.entries(columnFilters).map(
        ([column, { op, value }]) => `${column}:${op}:${value ?? ''}`
      ),
      ...(view?.params || {}),
      ...Object.fromEntries(
        Object.entries(filters).filter(([, v]) => v !== '' && v != null)
      ),
    };
  }, [views, activeView, page, limit, sort, debouncedQuery, filters, columnFilters]);

  const load = useCallback(async () => {
    if (!fetcher) return;
    requestSeq.current += 1;
    const seq = requestSeq.current;
    setLoading(true);
    setError(null);
    try {
      const result = await fetcher(params);
      // Ignore a slower earlier request that resolved after a newer one.
      if (seq !== requestSeq.current) return;
      if (Array.isArray(result)) {
        setItems(result);
        setTotal(result.length);
      } else {
        setItems(result?.items || []);
        setTotal(result?.total ?? (result?.items || []).length);
      }
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setError(err);
      setItems([]);
      setTotal(0);
    } finally {
      if (seq === requestSeq.current) setLoading(false);
    }
  }, [fetcher, params]);

  useEffect(() => {
    load();
  }, [load]);

  const loadCounts = useCallback(async () => {
    if (!countsFetcher) return;
    try {
      setCounts((await countsFetcher()) || {});
    } catch {
      // View-tab counts are decorative; a failure must not blank the screen.
    }
  }, [countsFetcher]);

  useEffect(() => {
    loadCounts();
  }, [loadCounts]);

  // Selection is cleared whenever the visible set changes, so a bulk action can
  // never apply to rows the admin can no longer see.
  useEffect(() => {
    setSelectedIds(new Set());
  }, [activeView, debouncedQuery, filters, columnFilters, page]);

  const toggleSort = useCallback((column) => {
    setSort((current) => {
      if (current?.column !== column) return { column, direction: 'asc' };
      if (current.direction === 'asc') return { column, direction: 'desc' };
      return null;
    });
  }, []);

  const toggleRow = useCallback((id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const toggleAll = useCallback(
    (ids) => {
      setSelectedIds((prev) => {
        const allSelected = ids.length > 0 && ids.every((id) => prev.has(id));
        return allSelected ? new Set() : new Set(ids);
      });
    },
    []
  );

  const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

  const clearFilters = useCallback(() => {
    setFilters(initialFilters);
    setColumnFilters({});
    setQuery('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFilter = useCallback((key, value) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }, []);

  /** `setColumnFilter('purchase_value', { op: 'gte', value: '1000' })`; null clears it. */
  const setColumnFilter = useCallback((column, filter) => {
    setColumnFilters((prev) => {
      const next = { ...prev };
      if (!filter) delete next[column];
      else next[column] = filter;
      return next;
    });
  }, []);

  const clearColumnFilters = useCallback(() => setColumnFilters({}), []);

  const hasActiveFilters = useMemo(
    () =>
      !!query ||
      Object.keys(columnFilters).length > 0 ||
      Object.values(filters).some((v) => v !== '' && v != null),
    [query, filters, columnFilters]
  );

  return {
    // view
    views,
    activeView,
    setActiveView,
    counts,
    // query
    filters,
    setFilter,
    setFilters,
    columnFilters,
    setColumnFilter,
    clearColumnFilters,
    clearFilters,
    hasActiveFilters,
    query,
    setQuery,
    sort,
    toggleSort,
    // paging
    page,
    setPage,
    limit,
    total,
    totalPages: Math.max(1, Math.ceil(total / limit)),
    // data
    items,
    loading,
    error,
    reload: load,
    reloadCounts: loadCounts,
    // selection
    selectedIds,
    toggleRow,
    toggleAll,
    clearSelection,
    // drawer
    drawerRecord,
    setDrawerRecord,
  };
}
